'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { ADMIN_TOKEN_KEY, API_BASE_URL } from '@/lib/api/config';
import { buildRequestBody, unwrapResponseData } from '@/lib/api/encryption';
import { ADMIN_TOKEN_COOKIE } from '@/lib/adminAuthConfig';
import { hasPermission } from '@/lib/permissions';

export type AdminRole = string | null;

export interface AdminRoleRef {
  id: string;
  name: string;
  slug: string;
}

interface AdminUser {
  id: string;
  email: string;
  name?: string;
  role: string;
  roleLabel: string;
  roles?: AdminRoleRef[];
  roleIds?: string[];
  permissions?: string[];
  active?: boolean;
}

interface AuthContextValue {
  user: AdminUser | null;
  session: AdminUser | null;
  loading: boolean;
  role: AdminRole;
  roleLabel: string;
  permissions: string[];
  roles: AdminRoleRef[];
  can: (permission: string) => boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function setAdminSessionToken(token: string) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  document.cookie = `${ADMIN_TOKEN_COOKIE}=${encodeURIComponent(token)}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
}

function clearAdminSessionToken() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  document.cookie = `${ADMIN_TOKEN_COOKIE}=; path=/; max-age=0`;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<AdminRole>(null);
  const [roleLabel, setRoleLabel] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<AdminRoleRef[]>([]);
  const initializedRef = useRef(false);

  const setAdminUser = (adminUser: AdminUser) => {
    setUser(adminUser);
    setRole(adminUser.role || null);
    setRoleLabel(adminUser.roleLabel || adminUser.role || '');
    setPermissions(adminUser.permissions ?? []);
    setRoles(adminUser.roles ?? []);
  };

  const clearAdminUser = () => {
    setUser(null);
    setRole(null);
    setRoleLabel('');
    setPermissions([]);
    setRoles([]);
  };

  const loadUserFromToken = async () => {
    try {
      const localToken =
        typeof window !== 'undefined' ? localStorage.getItem(ADMIN_TOKEN_KEY) : null;
      if (!localToken) {
        clearAdminUser();
        return;
      }

      const res = await fetch(`${API_BASE_URL}/admin/auth/me`, {
        headers: { Authorization: `Bearer ${localToken}` },
      });

      if (res.ok) {
        const json = await res.json();
        const payload = unwrapResponseData<{ user: AdminUser }>(json.data);
        setAdminUser(payload.user);
        return;
      }

      localStorage.removeItem(ADMIN_TOKEN_KEY);
      clearAdminSessionToken();
      clearAdminUser();
    } catch {
      clearAdminUser();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    loadUserFromToken();
  }, []);

  const signIn = async (email: string, password: string) => {
    const res = await fetch(`${API_BASE_URL}/auth/catalog-admin-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildRequestBody({ email, password })),
    });
    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.message || 'Login failed');
    }

    const data = unwrapResponseData<{ token?: string; user: AdminUser }>(json.data);
    if (data.token) {
      setAdminSessionToken(data.token);
    }
    setAdminUser(data.user);
    setLoading(false);
    return data;
  };

  const signOut = async () => {
    clearAdminSessionToken();
    clearAdminUser();
  };

  const can = (permission: string) => hasPermission(permissions, permission);

  return (
    <AuthContext.Provider
      value={{
        user,
        session: user,
        loading,
        role,
        roleLabel,
        permissions,
        roles,
        can,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
